import { Group, Stack, Text } from "@mantine/core"
import { Select } from "@tfg/ui"
import { useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { ProgressBox } from "~/pages/Brand/Home/components/ProgressBox.tsx"
import { useDashboardStats } from "~/pages/Brand/Home/hooks/useDashboardStats"
import { useFilteredData } from "~/pages/Brand/Home/hooks/useFilteredData"
import {
  IngestionDateFilter,
  useIngestionDateFilter,
} from "~/pages/Brand/Home/hooks/useIngestionDateFilter"

interface Props {
  products: LibraryResponses.AgecComplianceProducts[]
}

export const AgecCompliance = ({ products }: Props) => {
  const { t } = useTranslation("home")

  const {
    options: ingestionDateOptions,
    ingestionDateFilter,
    setIngestionDateFilter,
    satisfiesIngestionDateFilter,
  } = useIngestionDateFilter()

  const [brandTypeFilter, setBrandTypeFilter] = useState<string | null>(null)
  const [collection, setCollection] = useState<string | null>(null)

  const filteredProducts = useFilteredData({
    data: products,
    filters: [
      (product) => {
        return collection ? product.collection === collection : true
      },
      (product) => {
        return brandTypeFilter ? product.brandType === brandTypeFilter : true
      },
      (product) => satisfiesIngestionDateFilter(product.ingestionDate),
    ],
  })

  const stats = useDashboardStats(
    filteredProducts,
    (product) => product.agecCompliance
  )

  const collections = useMemo(
    () =>
      [
        ...new Set<string>(
          products
            .filter((product) => product.collection)
            .map((product): string => {
              return product.collection ?? "Unkown"
            })
        ),
      ]
        .map((collection) => {
          return {
            value: collection,
            label: collection,
          }
        })
        .sort(),
    [products]
  )

  const brandTypeOptions = useMemo(
    () =>
      [...new Set(products.map((product) => product.brandType))]
        .flatMap((type) => (type ? [type] : []))
        .sort()
        .flatMap((val) => {
          const ptype = products.find((p) => p.brandType == val)
          return ptype
            ? [
                {
                  label: val,
                  value: val,
                  type: ptype.type.name,
                },
              ]
            : []
        }),
    [products]
  )

  return (
    <Stack>
      <Group wrap="nowrap">
        <Select
          size="xs"
          clearable
          data={collections}
          value={collection}
          onChange={setCollection}
          placeholder={t("informationRequests.collection")}
        />
        <Select
          size="xs"
          clearable
          data={brandTypeOptions}
          value={brandTypeFilter}
          onChange={setBrandTypeFilter}
          placeholder={t("informationRequests.type")}
        />
        <Select
          size="xs"
          clearable
          data={ingestionDateOptions}
          value={ingestionDateFilter}
          onChange={(value) => {
            setIngestionDateFilter(value as IngestionDateFilter)
          }}
          placeholder={t("informationRequests.ingestionDate")}
        />
      </Group>
      {stats.total > 0 ? (
        <Stack bg="white" px="md" py="md">
          <ProgressBox
            sections={[
              {
                color: "green",
                text: stats.completedCount.toString(),
                width: stats.completedPercent,
                label: t("agecCompliance.compliant"),
                href: "/regulations/agec?agecCompliance=yes",
              },
              {
                color: "gray.4",
                text: stats.notCompletedCount.toString(),
                width: stats.notCompletedPercent,
                label: t("agecCompliance.nonCompliant"),
                href: "/regulations/agec?agecCompliance=no",
              },
            ]}
            total={stats.total}
            helpHoverText={t("agecCompliance.totalText")}
          />
          <Text fz="md">
            <Trans ns="home" i18nKey="informationRequests.complete">
              <strong>
                {{
                  percent: stats.completedPercent,
                }}
              </strong>
            </Trans>
          </Text>
        </Stack>
      ) : (
        <Text fz="md">
          <strong>{t("informationRequests.noProducts")}</strong>
        </Text>
      )}
    </Stack>
  )
}
